import { Link } from "wouter";

/**
 * NOT BUILT, AND WHY. One page, twenty five rows.
 *
 * Every screen in this console carried its own copy of this table, headed
 * "Not built, and why" or "Not wired, and why". The content is worth keeping:
 * it is the record of what the design asked for and what the product cannot
 * honestly show, and it is the reason nothing here is a hardcoded green badge.
 *
 * But it was written for whoever built the console, not for whoever is using
 * it. An admin opening Payments at nine in the morning does not need to be told
 * that regional edge nodes do not exist, and on Observability it sat under the
 * screen whose job is answering "is anything wrong right now". Five copies of a
 * changelog is four too many.
 *
 * Every row is here, unchanged, grouped by the screen it used to sit under.
 */

type Row = { readonly what: string; readonly why: string };
type Group = { readonly where: string; readonly rows: readonly Row[] };

const GROUPS: readonly Group[] = [
  {
    where: "Observability",
    rows: [
      {
        what: "Regional edge nodes, a mesh, a cluster switcher, a freeze",
        why: "The product is one Worker, one Pages project and one database. None of them reports a region or a replica count, and there is no read only mode to switch into.",
      },
      {
        what: "Uptime, an incident count, a success figure, a settlement promise",
        why: "Nothing measures uptime, nothing records an incident and nobody is paged. A badge that is hardcoded green says nothing true.",
      },
      {
        what: "A rubric version, a hotfix deploy, a calibration score",
        why: "There is no rubric with a version. A reading is one model call, and the model id it used is on the Models screen and on the debrief itself.",
      },
      {
        what: "Revenue and a target to measure it against",
        why: "Money is on Payments, in rupees, and that screen reads the gateway it was taken through. A target nobody set is not a denominator, so there is nothing here to measure revenue against.",
      },
    ],
  },
  {
    where: "Candidates",
    rows: [
      {
        what: "Impersonate a candidate, read only",
        why: "Impersonation means minting a candidate session from the admin origin, which is the one capability that could quietly write to somebody's account in their name. Read only would have to be enforced by the server, not promised by a label, and that is a threat model rather than a button.",
      },
      {
        what: "Invite a candidate cohort",
        why: "The product sends exactly one kind of email, the sign in link, from one address. There is no cohort, no template, no list, and nothing that would record who was invited or whether it arrived, so the button would send with no record of the sending.",
      },
      {
        what: "A flagged segment",
        why: "Nothing flags an account. There is no column and no rule that would set one, so the pill would always read zero.",
      },
      {
        what: "A bar raiser score, a rubric percentage, a median package",
        why: "Nothing here scores a candidate. The column that would hold a score holds what the person has logged instead: applications, rounds, debriefs, mocks.",
      },
    ],
  },
  {
    where: "Payments",
    rows: [
      {
        what: "Webhook retries, settlement timing, disputes",
        why: "The webhook endpoint exists and settles payments, and a repeat delivery is already harmless: settlement is guarded on the payment still being open, so a second arrival writes nothing. What is missing is the record. Nothing stores a delivery, a status or a failure, so there is no list here to retry from and no dispute feed to read.",
      },
      {
        what: "Invoices, credit notes, tax",
        why: "No invoice is generated anywhere in the product and GST treatment has not been decided. Issuing one before that decision is made would put a tax position in writing that nobody has taken.",
      },
      {
        what: "Payment method, card network, the last four digits",
        why: "Nothing records a method. Razorpay reports one on a captured payment, but this product neither reads it nor stores it, and what may be kept about an instrument is a decision nobody has written down yet.",
      },
      {
        what: "A gateway fee, and a net of fees",
        why: "No fee schedule exists because no account exists. Gross, refunded and net of refunds all ship, because all three are arithmetic on rows.",
      },
      {
        what: "Voucher eligibility by email domain, fraud guard, an edge key sync",
        why: "A voucher is a code, a benefit and a use count, which is what the table holds. Eligibility is a redemption feature and there is no redemption path yet.",
      },
      {
        what: "Stripe, dollars, a currency toggle",
        why: "One currency, INR, held in paise. A second would need a rate and a settlement account.",
      },
    ],
  },
  {
    where: "AI models",
    rows: [
      {
        what: "Interruption sensitivity, synthesis temperature, streaming bitrate",
        why: "Three sliders over machinery this product does not own. A mock is spoken and heard, but by the browser: the interviewer through speechSynthesis and the candidate through SpeechRecognition, both on the candidate machine, with no audio reaching a server and no stream to set a bitrate on. A slider that changes nothing is a lie with a handle on it.",
      },
      {
        what: "Rubric weighting matrix, save and publish a rubric version",
        why: "No code reads a weight, and there is no score for one to weigh. The reader returns findings that quote the account they came from, and the numbers out of 100 it used to return were removed because nothing measured them.",
      },
      {
        what: "Token spend, cost per reading",
        why: "Nothing records usage. The provider returns it on every call and the response is parsed for the model id only, so the figure would have to be stored before it could be shown.",
      },
      {
        what: "Speech accuracy, word error rate, hesitation and filler scores as live telemetry",
        why: "Filler words and silences are measured per transcript and already ship on the mock report. There is no population to average them over and no ground truth to score accuracy against.",
      },
      {
        what: "Latency, p95, active streams, regional relays, fleet consensus",
        why: "Nothing is timed and nothing is sharded. One Worker calls one provider.",
      },
      {
        what: "Adversarial stress test, purge audio buffers, switch voice engine, deploy rubric hotfix",
        why: "Four buttons over four subsystems that do not exist. No audio is retained to purge.",
      },
    ],
  },
  {
    where: "Audit and security",
    rows: [
      {
        what: "A proof that a row was not altered after it was written",
        why: "These are ordinary rows in the same database as everything else. Nothing signs one and nothing checks one against the row before it, so anybody with write access could change one and no code here would notice. The next step is written down in the migration that created this table, and it has not been built.",
      },
      {
        what: "A second sign in factor held in hardware",
        why: "Admin sign in is a signed cookie plus an address allowlist. There is no hardware key and no lockout counter, and a refused sign in is not recorded anywhere, so a count of them would be a zero nobody measured.",
      },
      {
        what: "A list of open sessions, and a button that ends them",
        why: "A session here is a stateless signed cookie, so there is no list of the ones that are open. One can be ended: signing out writes the session id to a denylist that every request checks. What cannot be done is ending a session nobody can name. No address and no location is recorded either.",
      },
      {
        what: "A compliance report, a grade, a named auditor",
        why: "Nobody has audited this product. A grade, an audit period and a firm's name would each be a claim with nothing at all behind it.",
      },
      {
        what: "A switch that freezes the product",
        why: "There is no read only mode and nothing that would put the product into one, so the button would have nothing to press against.",
      },
    ],
  },
];

const COUNT = GROUPS.reduce((sum, group) => sum + group.rows.length, 0);

export function NotBuiltScreen() {
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-space-lg">
      <header className="border-b border-outline-variant pb-space-md space-y-1">
        <div className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
          Executive supervision, the honest gaps
        </div>
        <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
          Not built, and why
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant max-w-3xl">
          {COUNT} things the design asked for that this console does not show.
          Each one is here because the machinery behind it does not exist, and a
          panel with nothing behind it is worse than an absent one. Nothing on
          this page is a roadmap: it is what would have to be true first.
        </p>
      </header>

      {GROUPS.map((group) => (
        <section key={group.where} className="space-y-3">
          <h2 className="font-headline-sm text-headline-sm font-bold text-on-surface">
            {group.where}
          </h2>
          <table className="w-full">
            <tbody className="divide-y divide-outline-variant/60">
              {group.rows.map((row) => (
                <tr key={row.what}>
                  <td className="py-3 pr-3 align-top font-body-sm text-body-sm font-semibold text-on-surface w-1/3">
                    {row.what}
                  </td>
                  <td className="py-3 align-top font-body-sm text-body-sm text-on-surface-variant">
                    {row.why}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <p className="font-body-sm text-body-sm text-on-surface-variant border-t border-outline-variant pt-space-md">
        <Link
          href="/observability"
          className="inline-flex items-center min-h-[24px] py-1 text-secondary font-semibold hover:underline"
        >
          Back to observability
        </Link>
      </p>
    </div>
  );
}
